import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import {
  CommentCell,
  DoctorNamingCell,
  PatientNamingCell,
} from 'src/pages/medical/patients/_components/patient-list-cell.component'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { arrayFilter, arrayUniqueByKey } from 'src/utils/array.util'
import { useQuery } from '@tanstack/react-query'
import { PatientService } from 'src/services/patient.service'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import PageTitle from 'src/components/page-title/page-title.component'
import { IUserOption } from 'src/components/select-medical-staff-member/select-medical-staff-member.component'
import { DataTableHeader } from './data-table-header.component'
import { TableReviews } from './table-reviews.component'

export default function DeprecatedReviewsPage() {
  const { projectId } = useParams()

  const {
    isLoading,
    isError,
    error,
    data: reviews,
  } = useQuery({
    queryKey: ['reviews', projectId],
    queryFn: () => PatientService.dreprecatedGetProjectReviews(Number(projectId)),
  })
  const { t } = useTranslation()

  const [filterOptions, setFilterOptions] = useState<IUserOption[]>()
  const [filter, setFilter] = useState('*')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (isLoading || isError || reviews?.length === 0) return undefined
    const filteredReviews = reviews?.filter((doctor: any) => doctor.User)
    const doctors = arrayUniqueByKey(
      filteredReviews?.map((review: any) => review?.User),
      'id',
    )

    const localFilterOptions = doctors.map((doctor) => ({
      label: `${doctor.firstname} ${doctor.lastname}`,
      value: doctor.id,
    }))
    localFilterOptions.push({ label: t('pages.reviews.all'), value: '*' })

    setFilterOptions(localFilterOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews, isLoading, isError])

  if (isError) return <ErrorQueryHandler error={error} />

  if (isLoading) {
    return <div className="loading" />
  }

  const cols = [
    {
      Header: t('pages.reviews.patient'),
      accessor: 'completeName',
      id: 'patient',
      align: 'left',
      Cell: ({ value }: any) => <PatientNamingCell completeName={value} />,
    },
    {
      Header: t('pages.reviews.referring-doctor'),
      accessor: 'User',
      id: 'doctor',
      align: 'left',
      Cell: ({ value }: any) => (
        <DoctorNamingCell title={value?.title} firstname={value?.firstname} lastname={value?.lastname} />
      ),
    },
    {
      Header: t('pages.reviews.surgery-type'),
      accessor: 'PatientMedEvents',
      id: 'med-event-type',
      align: 'left',
      Cell: ({ value: medEvents }: any) => {
        if (medEvents.length === 0) return <div>{t('pages.reviews.no-medevent')}</div>
        const { type } = medEvents[medEvents.length - 1].MedEvent
        return <div>{t('common.med-events-type', { medEventType: type })}</div>
      },
    },
    {
      Header: t('pages.reviews.date-of-surgery'),
      accessor: 'PatientMedEvents',
      id: 'med-event-date',
      align: 'left',
      Cell: ({ value: medEvents }: any) => {
        if (medEvents.length === 0) return <div>{t('pages.reviews.no-medevent')}</div>

        return <div>{getFormattedDate(medEvents[medEvents.length - 1].date)}</div>
      },
    },
    {
      Header: t('pages.reviews.reviews'),
      accessor: 'PatientVariables',
      id: 'comment-text',
      align: 'center',
      cellClass: 'text-center',
      Cell: ({ value: comments }: any) => <CommentCell comment={comments[comments.length - 1]} />,
    },
  ]

  const reviewsWithCompleteName =
    reviews?.length &&
    reviews?.map((review: any) => ({
      ...review,
      completeName: `${review.firstname} ${review.lastname}`,
    }))

  const sortList = (list: any) => {
    if (!list) return
    return list.sort(
      (review1: any, review2: any) =>
        new Date(review2.PatientVariables[review2.PatientVariables.length - 1].updatedAt).getTime() -
        new Date(review1.PatientVariables[review1.PatientVariables.length - 1].updatedAt).getTime(),
    )
  }

  let filteredReviews =
    reviews?.length !== 0 && arrayFilter(reviewsWithCompleteName, 'completeName', search, 'includes') // filter by patient name
  filteredReviews = reviews?.length !== 0 && arrayFilter(filteredReviews, 'User.id', filter, '===') // filter by doc id (option selector)
  const sortedReviews = sortList(filteredReviews)

  return (
    <>
      {!isLoading && !error && reviews && (
        <div className="reviews-list-wrapper">
          <PageTitle>{t('pages.reviews.reviews-list')}</PageTitle>
          <div className="c-w-full c-mb-3">
            <DataTableHeader
              filterOptions={filterOptions}
              onFilterSelect={setFilter}
              onSearch={setSearch}
              sortOptions={undefined}
              sort={undefined}
              onSortSelect={undefined}
            />
            <div className="c-border-b c-border-b-grey-200" />
          </div>

          <TableReviews columns={cols} data={sortedReviews ?? reviews} />
        </div>
      )}
    </>
  )
}
